import { IconButton } from "@mui/material";
import infoIcon from "Benchmarking/Assets/information.png";
import institutionIcon from "Benchmarking/Assets/institution.png";
import titleIcon from "Benchmarking/Assets/title.png";
import {
  FacultyPeers,
  FacultyPublications,
} from "Benchmarking/Components/FacultyProfileTabComponents";
import HorizontalBar from "Benchmarking/Components/HorizontalBar";
import KeywordCloudD3 from "Benchmarking/Components/KeywordCloudD3";
import {
  LoadingCircleImage,
  LoadingParagraph,
} from "Benchmarking/Components/LoadingComponent";
import { PaginationAdjustComponentBenchmarking } from "Benchmarking/Components/PaginationComponent";
import VerticalBar from "Benchmarking/Components/VerticalBar";
import "Benchmarking/Scss/FacultyDetail.scss";
import {
  getFacultyAdvanceStatisticById,
  getFacultyBaseStatisticById,
  getFacultyConceptMapById,
  getFacultyPeersById,
  getFacultyPublicationsById,
  getFacultySummaryById,
} from "Benchmarking/Services/Author";
import { Popover, Statistic, Tabs, Tooltip } from "antd";
import { useEffect, useState } from "react";
import CountUp from "react-countup";
import { useLocation } from "react-router-dom";

const formatter = (value) => <CountUp end={value} separator="," />;

const FacultyDetail = () => {
  const faculty_id = useLocation().pathname.split("/")[4];
  const [summary, setSummary] = useState();
  const [seeMore, setSeeMore] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
  });
  const [publications, setPublications] = useState();
  const [peers, setPeers] = useState();
  const [tab, setTab] = useState("publications");
  const [basicStat, setBasicStat] = useState({});
  const [advanceStat, setAdvanceStat] = useState({});
  const [citationStatistic, setCitationStatistic] = useState([]);
  //concept word cloud
  const [concept, setConcept] = useState([]);
  const [granularityLevel, setGranularityLevel] = useState(1);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedKeywordsQueue, setSelectedKeywordsQueue] = useState([]);
  const [fade, setFade] = useState("");

  //slider
  const [sliderValue, setSliderValue] = useState([1970, 2024]);

  //init data - slider
  useEffect(() => {
    getFacultySummaryById(faculty_id, (res) => {
      setSummary(res);
    });

    getFacultyBaseStatisticById(faculty_id, (res) => {
      setBasicStat(res);
      const citation_statistic = Object.keys(res.citation_by_publish_year).map(
        (key) => {
          return {
            Year: key,
            Citations: res.citation_by_publish_year[key],
          };
        }
      );
      setCitationStatistic(citation_statistic);
    });
    getFacultyAdvanceStatisticById(faculty_id, (res) => {
      setAdvanceStat({
        labels: ["Citations", "Publications", "H-index"],
        data: [
          res.citation_count_zscore,
          res["h-index_zscore"],
          res.number_of_publications_zscore,
        ],
      });
    });
  }, []);

  useEffect(() => {
    if (selectedKeywordsQueue[selectedKeywordsQueue.length - 1]) {
      let params = new URLSearchParams();
      selectedKeywordsQueue[selectedKeywordsQueue.length - 1].forEach(
        (keyword) => {
          if (keyword) {
            params.append("rootConcept", keyword.real_id);
          }
        }
      );
      getFacultyConceptMapById(
        faculty_id,
        sliderValue[0],
        sliderValue[1],
        granularityLevel,
        params.toString(),
        (res) => {
          setConcept(res.keywords);
          setFade("fade-in");
        }
      );
    } else {
      getFacultyConceptMapById(
        faculty_id,
        sliderValue[0],
        sliderValue[1],
        granularityLevel,
        null,
        (res) => {
          setConcept(res.keywords);
          setFade("fade-in");
        }
      );
    }
  }, [sliderValue, granularityLevel]);

  useEffect(() => {
    if (tab == "publications") {
      setPublications();
      const keywordsForFiltering =
        selectedKeywordsQueue && !selectedKeywords
          ? [...selectedKeywords, ...selectedKeywordsQueue.flat(Infinity)]
          : selectedKeywords;
      getFacultyPublicationsById(
        faculty_id,
        pagination.page,
        pagination.rowsPerPage,
        sliderValue[0],
        sliderValue[1],
        keywordsForFiltering,
        (res) => {
          setPublications(res.publications);
          setPagination({ ...pagination, totalRows: res.total });
        }
      );
    }
  }, [
    selectedKeywords,
    sliderValue,
    pagination.page,
    pagination.rowsPerPage,
    tab,
  ]);

  useEffect(() => {
    if (tab == "peers") {
      setPeers();
      getFacultyPeersById(
        faculty_id,
        pagination.page,
        pagination.rowsPerPage,
        (res) => {
          setPeers(res.peers);
          setPagination({ ...pagination, totalRows: res.total });
        }
      );
    }
  }, [pagination.page, pagination.rowsPerPage, tab]);

  const onChange = (key) => {
    setTab(key);
    setPagination({
      page: 1,
      rowsPerPage: 10,
    });
  };
  const tabs = [
    {
      label: "Publications",
      key: "publications",
      children: FacultyPublications,
      data: publications,
    },
    {
      label: "Peers",
      key: "peers",
      children: FacultyPeers,
      data: peers,
    },
  ];

  return (
    <div className="faculty-detail-benchmarking">
      <div className="basic-information">
        <div className="bio-faculty">
          <div className="avatar">
            <LoadingCircleImage
              width={120}
              height={120}
              data={summary?.photo}
            />
            {summary?.verified && (
              <svg
                className="check"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM16.592 9.46049C16.8463 9.13353 16.7874 8.66232 16.4605 8.40802C16.1335 8.15372 15.6623 8.21262 15.408 8.53958L11.401 13.6914C11.3119 13.806 11.1443 13.8209 11.0364 13.7238L8.50173 11.4426C8.19385 11.1655 7.71963 11.1904 7.44254 11.4983C7.16544 11.8062 7.1904 12.2804 7.49828 12.5575L10.033 14.8387C10.7881 15.5183 11.9613 15.4143 12.585 14.6123L16.592 9.46049Z"
                  fill="var(--benchmarking-color)"
                />
              </svg>
            )}
          </div>
          <div className="name">
            <LoadingParagraph
              rows={1}
              data={summary?.name}
              component={<p>{summary?.name}</p>}
            />
          </div>
          <div className="links">
            <Tooltip title={"Personal page @ " + summary?.homepage}>
              <IconButton
                aria-label="Personal web"
                size="small"
                target="_blank"
                href={summary?.homepage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20.3607 18.0108C20.3607 20.214 18.4891 22 16.1804 22H7.81965C5.5109 22 3.6393 20.214 3.6393 18.0108V13.133C3.6393 12.4248 3.34447 11.7456 2.81969 11.2448C1.60381 10.0845 1.76187 8.16205 3.15251 7.19692L9.54124 2.763C11.0071 1.74567 12.9929 1.74567 14.4588 2.763L20.8475 7.19691C22.2381 8.16205 22.3962 10.0845 21.1803 11.2448C20.6555 11.7456 20.3607 12.4248 20.3607 13.133V18.0108ZM12 18C13.1046 18 14 17.1046 14 16C14 14.8954 13.1046 14 12 14C10.8954 14 10 14.8954 10 16C10 17.1046 10.8954 18 12 18Z"
                    fill="var(--benchmarking-color)"
                  />
                </svg>
              </IconButton>
            </Tooltip>

            <Tooltip title={"Google scholar @ " + summary?.googlescholar}>
              <IconButton
                aria-label="Google scholar"
                size="small"
                target="_blank"
                href={summary?.googlescholar}
                disabled={summary?.googlescholar === null}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.84013 5.06253C9.48071 5.96842 9.18818 7.0688 8.99051 8.30415C9.9469 8.21655 10.9574 8.16992 12 8.16992C13.0427 8.16992 14.0531 8.21655 15.0095 8.30415C14.8119 7.0688 14.5193 5.96842 14.1599 5.06253C13.8144 4.19183 13.4221 3.53861 13.0253 3.11559C12.63 2.6942 12.2833 2.55266 12 2.55266C11.7168 2.55266 11.37 2.6942 10.9748 3.11559C10.5779 3.53861 10.1856 4.19183 9.84013 5.06253ZM8.42925 4.49357C7.98078 5.62389 7.63666 6.9855 7.42575 8.49077C6.38755 8.64513 5.44011 8.8511 4.61837 9.09963C3.66224 9.38882 2.83312 9.74837 2.22609 10.1824C2.1494 10.2373 2.0737 10.2952 2.00005 10.3563C2.67009 6.18086 5.85114 2.8536 9.9309 2C9.91066 2.02076 9.89059 2.04175 9.8707 2.06296C9.29791 2.67358 8.81709 3.51604 8.42925 4.49357ZM14.0691 2C14.0894 2.02076 14.1094 2.04175 14.1293 2.06296C14.7021 2.67358 15.183 3.51604 15.5708 4.49357C16.0193 5.62389 16.3634 6.9855 16.5743 8.49077C17.6125 8.64513 18.5599 8.8511 19.3817 9.09963C20.3378 9.38882 21.1669 9.74837 21.774 10.1824C21.8506 10.2373 21.9263 10.2953 22 10.3564C21.33 6.18086 18.1489 2.85359 14.0691 2ZM22 13.6433C21.9264 13.7045 21.8507 13.7624 21.774 13.8173C21.1669 14.2514 20.3378 14.6109 19.3817 14.9001C18.5599 15.1486 17.6125 15.3546 16.5743 15.509C16.3634 17.0142 16.0193 18.3758 15.5708 19.5062C15.183 20.4837 14.7021 21.3262 14.1293 21.9368C14.1093 21.9581 14.0892 21.9792 14.0688 22C18.1488 21.1465 21.3301 17.8191 22 13.6433ZM9.9312 22C9.91086 21.9792 9.89069 21.9581 9.8707 21.9368C9.29791 21.3262 8.81709 20.4837 8.42925 19.5062C7.98078 18.3758 7.63666 17.0142 7.42575 15.509C6.38755 15.3546 5.44011 15.1486 4.61837 14.9001C3.66223 14.6109 2.83312 14.2514 2.22609 13.8173C2.14938 13.7624 2.07367 13.7045 2 13.6433C2.66996 17.8191 5.8512 21.1465 9.9312 22ZM5.05502 13.4327C5.6995 13.6276 6.44103 13.7966 7.25833 13.9322C7.21206 13.304 7.18805 12.6581 7.18805 11.9999C7.18805 11.3416 7.21206 10.6957 7.25833 10.0675C6.44103 10.2031 5.6995 10.3721 5.05502 10.567C4.1779 10.8323 3.52343 11.133 3.10521 11.432C2.66725 11.7452 2.62933 11.9482 2.62933 11.9999C2.62933 12.0515 2.66725 12.2545 3.10521 12.5677C3.52343 12.8668 4.1779 13.1674 5.05502 13.4327ZM8.70762 11.9999C8.70762 12.7365 8.73947 13.4518 8.79938 14.1379C9.79829 14.2411 10.8749 14.2978 12 14.2978C13.1252 14.2978 14.2018 14.2411 15.2007 14.1379C15.2606 13.4518 15.2924 12.7365 15.2924 11.9999C15.2924 11.2633 15.2606 10.5479 15.2007 9.86187C14.2018 9.7586 13.1252 9.7019 12 9.7019C10.8749 9.7019 9.79829 9.7586 8.79938 9.86187C8.73947 10.5479 8.70762 11.2633 8.70762 11.9999ZM9.84013 18.9372C9.48071 18.0313 9.18818 16.9309 8.99051 15.6956C9.9469 15.7832 10.9574 15.8298 12 15.8298C13.0427 15.8298 14.0531 15.7832 15.0095 15.6956C14.8119 16.9309 14.5193 18.0313 14.1599 18.9372C13.8144 19.8079 13.4221 20.4611 13.0253 20.8841C12.63 21.3055 12.2833 21.4471 12 21.4471C11.7168 21.4471 11.37 21.3055 10.9748 20.8841C10.5779 20.4611 10.1856 19.8079 9.84013 18.9372ZM18.945 13.4327C18.3005 13.6276 17.559 13.7966 16.7417 13.9322C16.788 13.304 16.812 12.6581 16.812 11.9999C16.812 11.3416 16.788 10.6957 16.7417 10.0675C17.559 10.2031 18.3005 10.3721 18.945 10.567C19.8221 10.8323 20.4766 11.133 20.8948 11.432C21.3328 11.7452 21.3707 11.9482 21.3707 11.9999C21.3707 12.0515 21.3328 12.2545 20.8948 12.5677C20.4766 12.8668 19.8221 13.1674 18.945 13.4327Z"
                    fill="var(--benchmarking-color)"
                  />
                </svg>
              </IconButton>
            </Tooltip>
          </div>
          <div className="work-history">
            <div className="line">
              <img src={institutionIcon} alt="institution" />
              <LoadingParagraph
                component={<p>{summary?.department?.inst_name}</p>}
                data={summary?.department?.inst_name}
              />
            </div>

            <div className="line">
              <img src={titleIcon} alt="title" />
              <LoadingParagraph
                component={
                  <Popover
                    content={
                      <div
                        style={{
                          width: "400px",
                          height: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {summary?.position}
                      </div>
                    }
                    title={summary?.name}
                    trigger="click"
                  >
                    <div className={"description"}>
                      <p>{summary?.position}</p>
                    </div>
                  </Popover>
                }
                data={summary?.position}
              />
            </div>

            <div className="line">
              <img src={infoIcon} alt="information" />
              <LoadingParagraph
                component={
                  <Popover
                    content={
                      <div
                        style={{
                          width: "400px",
                          height: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {summary?.biography}
                      </div>
                    }
                    title={summary?.name}
                    trigger="click"
                  >
                    <div className={"description"}>
                      <p>{summary?.biography}</p>
                      {!seeMore && (
                        <p
                          className="see-more"
                          onClick={() => setSeeMore(true)}
                        >
                          See more
                        </p>
                      )}
                      {seeMore && (
                        <p
                          className="see-more"
                          onClick={() => setSeeMore(false)}
                        >
                          See less
                        </p>
                      )}
                    </div>
                  </Popover>
                }
                data={summary?.biography}
              />
            </div>
          </div>
        </div>
        <div className="faculty-detail-word-map">
          <KeywordCloudD3
            keywords={concept}
            selectedKeywords={selectedKeywords}
            setSelectedKeywords={setSelectedKeywords}
            granularityLevel={granularityLevel}
            setGranularityLevel={setGranularityLevel}
            selectedKeywordsQueue={selectedKeywordsQueue}
            setSelectedKeywordsQueue={setSelectedKeywordsQueue}
            sliderValue={sliderValue}
            setSliderValue={setSliderValue}
            bigCloud={false}
          />
        </div>
      </div>
      <div className="academic-result">
        <div className="basic-statistic-faculty">
          <p
            style={{
              fontWeight: "bold",
              fontSize: "24px",
            }}
          >
            Statistic
          </p>
          <div className="base">
            <Statistic
              className="stat-card"
              title="Publications"
              value={basicStat.number_of_publications}
              formatter={formatter}
            />

            <Statistic
              className="stat-card"
              title="Citations"
              value={basicStat.citation_count}
              formatter={formatter}
            />
            <Statistic
              className="stat-card"
              title="H index"
              value={basicStat["h-index"]}
              formatter={formatter}
            />
          </div>
          <div className="stat-graph">
            <div className="citation-by-year">
              <p className="chart-title">Citations by year</p>

              <VerticalBar data={citationStatistic} />
            </div>
            <div className="advance-param">
              <p className="chart-title">Research z-score</p>
              <HorizontalBar advance_stat={advanceStat} />
            </div>
          </div>
        </div>
        <div className="top-result">
          <Tabs
            onChange={onChange}
            type="card"
            items={tabs.map((item) => {
              return {
                label: item.label,
                key: item.key,
                children: <item.children data={item.data} />,
              };
            })}
          />

          <PaginationAdjustComponentBenchmarking
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
      </div>
    </div>
  );
};

export default FacultyDetail;
